import { Outlet, useLocation, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Crown, Loader2, Sparkles } from 'lucide-react';
import { useUIStore } from '@/contexts/uiStore';
import { useIsFreeUser } from '@/api/hooks/useSubscription';
import { useAuthStore } from '@/contexts/authStore';
import { useProjects } from '@/api/hooks/useProjects';
import { queryKeys } from '@/api/queryKeys';
import {
  getRecommendedPlan,
  clearRecommendedPlan,
  shouldShowPlanModal,
  clearShowPlanModal
} from '@/lib/recommendedPlan';
import { getFunnelContext, INSTAGRAM_TRANSCRIPTION_FUNNEL } from '@/lib/funnelToken';
import Sidebar from './Sidebar';
import PostAuthPlanModal from './PostAuthPlanModal';
import FunnelClaimWatcher from './FunnelClaimWatcher';

export default function DashboardLayout() {
  const { sidebarCollapsed } = useUIStore();
  const { isFree, isLoading, subscription } = useIsFreeUser();
  const { user } = useAuthStore();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { projectId } = useParams<{ projectId?: string }>();
  const { data: projects = [], isLoading: isProjectsLoading } = useProjects();

  const [showPlanModal, setShowPlanModal] = useState(false);
  const [recommendedPlan, setRecommendedPlanState] = useState<string | undefined>();

  const currentProject = projectId ? projects.find((project) => project.id === projectId) : undefined;
  const isGlobalAdmin = !!user?.is_global_admin;
  const isOwnProject = !!currentProject && currentProject.owner_id === user?.uid;
  const canShowPlanModalInContext = !projectId || isOwnProject;
  const isProjectContextLoading = !!projectId && isProjectsLoading;

  const isInstagramFunnelContext = () =>
    new URLSearchParams(location.search).get('funnel') === INSTAGRAM_TRANSCRIPTION_FUNNEL ||
    getFunnelContext()?.source === INSTAGRAM_TRANSCRIPTION_FUNNEL;

  const handleMainNavigationAttempt = () => {
    if (isGlobalAdmin || !isFree || !canShowPlanModalInContext || !isInstagramFunnelContext()) {
      return false;
    }

    const plan = getRecommendedPlan() || undefined;
    setRecommendedPlanState(plan);
    setShowPlanModal(true);
    return true;
  };

  // Detectar se deve mostrar o modal de planos (via sessionStorage flag)
  useEffect(() => {
    if (isLoading || isProjectContextLoading) return;

    const searchParams = new URLSearchParams(location.search);
    const checkoutStatus = searchParams.get('checkout');
    const isCheckoutSuccess = checkoutStatus === 'success';

    if (isCheckoutSuccess) {
      setShowPlanModal(false);
      clearShowPlanModal();
      clearRecommendedPlan();
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscription });
      return;
    }

    if (isGlobalAdmin) {
      clearShowPlanModal();
      clearRecommendedPlan();
      setShowPlanModal(false);
      return;
    }

    const isInstagramFunnel = isInstagramFunnelContext();
    const shouldShow = shouldShowPlanModal() || isInstagramFunnel;
    if (shouldShow) {
      // Limpar o flag imediatamente para não mostrar novamente em navegações futuras
      clearShowPlanModal();

      if (isFree && canShowPlanModalInContext) {
        // Usuário é free, mostrar modal
        const plan = getRecommendedPlan() || undefined;
        setRecommendedPlanState(plan);
        setShowPlanModal(true);
      } else {
        // Usuário já tem plano pago, limpar recommended plan
        clearRecommendedPlan();
      }
    }
  }, [isFree, isLoading, canShowPlanModalInContext, isProjectContextLoading, location.search, queryClient, isGlobalAdmin]);

  const isAdminRoute = location.pathname.startsWith('/admin/');
  const requiresSubscription =
    !isLoading &&
    !isProjectContextLoading &&
    !!projectId &&
    isOwnProject &&
    isFree &&
    !isGlobalAdmin &&
    !isAdminRoute;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Sidebar onMainNavigationAttempt={handleMainNavigationAttempt} />
      <main className={`${sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'} min-h-screen transition-all duration-300`}>
        {isLoading || isProjectContextLoading ? (
          <div className="min-h-screen flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : requiresSubscription ? (
          <div className="min-h-screen flex items-center justify-center px-6 py-12">
            <div className="max-w-xl text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Crown className="h-8 w-8" />
              </div>
              <h1 className="text-3xl font-bold text-foreground">
                Escolha um plano para continuar
              </h1>
              <p className="mt-3 text-muted-foreground">
                O Content4Life agora funciona com assinatura obrigatória. Você pode testar qualquer plano por 7 dias e cancelar antes da primeira cobrança.
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                Status atual: {subscription?.status || 'requires_subscription'}
              </p>
              <button
                onClick={() => setShowPlanModal(true)}
                className="mt-8 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-bold text-white shadow-lg shadow-primary/25 transition hover:bg-primary-hover"
              >
                <Sparkles className="h-4 w-4" />
                Escolher plano
              </button>
            </div>
          </div>
        ) : (
          <Outlet />
        )}
      </main>

      <PostAuthPlanModal
        isOpen={showPlanModal}
        recommendedPlan={recommendedPlan}
        onClose={() => setShowPlanModal(false)}
      />

      <FunnelClaimWatcher />
    </div>
  );
}
